import { AlgoliaHit } from '@repo/algolia/types'
import { LocaleIdentifier } from '@repo/markets/types'
import { SHOPIFY_ATTRIBUTES } from '@repo/shopify/attributes'
import { currencyCodeFallback, getPriceCurrency } from '@repo/shopify/price'
import { Cart, CartItem, Product, ProductVariant } from '@repo/shopify/types'
import { getFromStorage, LOCAL_STORAGE_LIST } from '@repo/storage/service'
import { createProductUrl } from '@repo/url/url'
import { extractLastPart } from '@repo/utils/text'
import {
  ElevarAddToCartEventData,
  ElevarProduct,
  ElevarRemoveFromCartEventData,
  ElevarSelectItemEventData,
  ElevarUserDataEventData,
  ElevarViewCartEventData,
  ElevarViewItemEventData,
  ElevarViewItemListEventData,
  ElevarViewSearchResultsEventData,
} from './types'

const brandFallback: Readonly<string> = 'Miista'
const listFallback: Readonly<string> = 'unknown'
const categoryFallback: Readonly<string> = 'Unknown'
const priceFallback: Readonly<string> = '0.0'

/**
 * Map into Elevar object.
 *
 * Please note, for consistency in tracking, we prefer to
 * send Shopify data into the data layer, not Sanity data.
 */
export const mapCartToElevarUserData = (cart: Cart, locale: LocaleIdentifier): Omit<ElevarUserDataEventData, 'user_properties'> => {
  return {
    cart_total: parseFloat(cart.cost.totalAmount.amount || priceFallback).toFixed(2),
    ecommerce: {
      currencyCode: cart.cost.totalAmount.currencyCode || currencyCodeFallback,
      cart_contents: {
        products: cart.lines.map((line, i) => mapLineItem(line, locale, i)),
      },
    },
  }
}

/**
 * Map into Elevar object.
 *
 * Please note, for consistency in tracking, we prefer to
 * send Shopify data into the data layer, not Sanity data.
 */
export const mapCollectionToElevarViewItemListData = (
  products: Product[],
  locale: LocaleIdentifier,
  list?: string,
  // Send max 25 products to Elevar, or the impressions data becomes too large
  limit = 25,
): Omit<ElevarViewItemListEventData, 'user_properties'> => {
  return {
    ecommerce: {
      currencyCode: getPriceCurrency(products?.[0]),
      impressions: products
        // Slice a bit more since we flatMap down the road
        .slice(0, limit + 5)
        .flatMap((product, i) => {
          const { variant, price, compareAtPrice, discountAmount } = extractVariant(product)

          if (!variant) {
            return []
          }

          return {
            id: variant.sku,
            name: product.title,
            brand: product.vendor,
            category: product.productType || categoryFallback,
            variant: variant.title,
            price: price.toFixed(2),
            position: i + 1,
            list: list || listFallback,
            product_id: extractLastPart(product.id),
            variant_id: extractLastPart(variant.id),
            compare_at_price: discountAmount > 0 ? compareAtPrice.toFixed(2) : priceFallback,
            image: product.featuredImage?.url || '',
            url: createProductUrl(product.handle, locale),
          }
        })
        // Final subset
        .slice(0, limit),
    },
  }
}

/**
 * Map into Elevar object.
 *
 * Please note, for consistency in tracking, we prefer to
 * send Shopify data into the data layer, not Sanity data.
 */
export const mapCartToElevarViewCartData = (cart: Cart, locale: LocaleIdentifier): Omit<ElevarViewCartEventData, 'user_properties'> => {
  return {
    cart_total: parseFloat(cart.cost.totalAmount.amount || priceFallback).toFixed(2),
    ecommerce: {
      currencyCode: cart.cost.totalAmount.currencyCode || currencyCodeFallback,
      actionField: {
        list: 'Shopping Cart',
      },
      impressions: cart.lines.map((line, i) => {
        const base = mapLineItem(line, locale, i)

        return {
          ...base,
          position: base.position || i + 1,
          url: base.url || createProductUrl(line.merchandise.product.handle, locale),
        }
      }),
    },
  }
}

/**
 * Map into Elevar object.
 *
 * Please note, for consistency in tracking, we prefer to
 * send Shopify data into the data layer, not Sanity data.
 */
export const mapProductToElevarSelectItemData = (product: Product, index: number, list?: string): Omit<ElevarSelectItemEventData, 'user_properties'> => {
  const { variant, price, compareAtPrice, discountAmount } = extractVariant(product)

  const products = variant
    ? [
        {
          id: variant.sku,
          name: product.title,
          brand: product.vendor,
          category: product.productType || categoryFallback,
          variant: variant.title,
          price: price.toFixed(2),
          position: index + 1,
          list: list || listFallback,
          product_id: extractLastPart(product.id),
          variant_id: extractLastPart(variant.id),
          compare_at_price: discountAmount > 0 ? compareAtPrice.toFixed(2) : priceFallback,
          image: product.featuredImage?.url || '',
        },
      ]
    : []

  return {
    ecommerce: {
      currencyCode: getPriceCurrency(product),
      click: {
        actionField: {
          action: 'click',
          list: list || listFallback,
        },
        products,
      },
    },
  }
}

/**
 * Map into Elevar object.
 *
 * Please note, for consistency in tracking, we prefer to
 * send Shopify data into the data layer, not Sanity data.
 */
export const mapProductToElevarViewItemData = (product: Product): Omit<ElevarViewItemEventData, 'user_properties'> => {
  const list = String(getFromStorage(LOCAL_STORAGE_LIST) || listFallback)
  const { variant, price, compareAtPrice, discountAmount } = extractVariant(product)

  const products: ElevarProduct[] = variant
    ? [
        {
          list,
          id: variant.sku,
          name: String(product.title || 'Unknown'),
          brand: product.vendor || brandFallback,
          category: product.productType || categoryFallback,
          variant: variant.title,
          price: price.toFixed(2),
          product_id: extractLastPart(String(product.id)),
          variant_id: extractLastPart(variant.id),
          compare_at_price: discountAmount > 0 ? compareAtPrice.toFixed(2) : priceFallback,
          image: product.featuredImage?.url || '',
        },
      ]
    : []

  return {
    ecommerce: {
      currencyCode: getPriceCurrency(product),
      detail: {
        actionField: {
          action: 'detail',
          list,
        },
        products,
      },
    },
  }
}

/**
 * Map into Elevar object.
 *
 * Please note, for consistency in tracking, we prefer to
 * send Shopify data into the data layer, not Sanity data.
 */
export const mapProductToElevarAddToCartData = (product: Product, variant: ProductVariant, locale: LocaleIdentifier): Omit<ElevarAddToCartEventData, 'user_properties'> => {
  const list = String(getFromStorage(LOCAL_STORAGE_LIST) || listFallback)
  const price = parseFloat(variant?.price.amount || priceFallback)
  const compareAtPrice = parseFloat(variant?.compareAtPrice?.amount || variant?.price?.amount || priceFallback)
  const discountAmount = compareAtPrice > price ? compareAtPrice - price : 0

  const products = [
    {
      list,
      id: variant.sku,
      name: product.title,
      brand: product.vendor,
      category: product.productType,
      variant: variant.title,
      price: price.toFixed(2),
      product_id: extractLastPart(product.id),
      variant_id: extractLastPart(variant.id),
      compare_at_price: discountAmount > 0 ? compareAtPrice.toFixed(2) : priceFallback,
      image: product.featuredImage?.url || '',
      quantity: '1',
      url: createProductUrl(product.handle, locale),
    },
  ]

  return {
    ecommerce: {
      currencyCode: getPriceCurrency(product),
      add: {
        actionField: {
          list,
        },
        products,
      },
    },
  }
}

/**
 * Map into Elevar object.
 *
 * Please note, for consistency in tracking, we prefer to
 * send Shopify data into the data layer, not Sanity data.
 */
export const mapCartItemToElevarAddToCartData = (cartItem: CartItem, locale: LocaleIdentifier, quantity?: number): Omit<ElevarAddToCartEventData, 'user_properties'> => {
  const base = mapLineItem(cartItem, locale)

  const product = {
    ...base,
    quantity: String(quantity || 1),
    // Keep Typescript compiler happy
    url: base.url || createProductUrl(cartItem.merchandise.product.handle, locale),
  }

  return {
    ecommerce: {
      currencyCode: cartItem?.merchandise?.product?.priceRange?.minVariantPrice?.currencyCode || currencyCodeFallback,
      add: {
        actionField: {
          list: product.list,
        },
        products: [product],
      },
    },
  }
}

export const mapCartItemToElevarRemoveFromCartData = (cartItem: CartItem, locale: LocaleIdentifier, quantity?: number): Omit<ElevarRemoveFromCartEventData, 'user_properties'> => {
  const product = {
    ...mapLineItem(cartItem, locale),
    // If quantity not provided, assume whole cart line is removed
    quantity: String(quantity || cartItem.quantity),
  }

  return {
    ecommerce: {
      currencyCode: cartItem?.merchandise?.product?.priceRange?.minVariantPrice?.currencyCode || currencyCodeFallback,
      remove: {
        actionField: {
          list: product.list,
        },
        products: [product],
      },
    },
  }
}

/**
 * Map into Elevar object.
 */
export const mapAlgoliaHitsToElevarViewSearchResultsData = (hits: AlgoliaHit[], locale: LocaleIdentifier): Omit<ElevarViewSearchResultsEventData, 'user_properties'> => {
  return {
    ecommerce: {
      currencyCode: hits?.[0]?.shopifyCurrency || currencyCodeFallback,
      actionField: {
        list: 'search results',
      },
      impressions: hits.flatMap((hit, i) => {
        const variant = hit.shopifyVariants?.find((variant) => variant?.availableForSale) || hit.shopifyVariants?.[0]

        if (!variant) {
          return []
        }

        return {
          id: variant.sku,
          name: hit.title || '',
          brand: hit.shopifyVendor || brandFallback,
          category: hit.shopifyProductType || categoryFallback,
          variant: variant.title,
          price: hit.shopifyPrice.toFixed(2),
          position: i + 1,
          list: 'search results',
          product_id: extractLastPart(hit.shopifyProductId),
          variant_id: extractLastPart(variant.id),
          compare_at_price: hit.shopifyDiscount > 0 ? (hit.shopifyPrice + hit.shopifyDiscount).toFixed(2) : priceFallback,
          image: hit.shopifyFeaturedImage || '',
          url: createProductUrl(hit.urlSlug, locale),
        }
      }),
    },
  }
}

const mapLineItem = (line: Cart['lines'][number], locale: LocaleIdentifier, index?: number): ElevarProduct => {
  const price = parseFloat(line.merchandise.price.amount)
  const compareAtPrice = parseFloat(line.merchandise.compareAtPrice?.amount || line.merchandise.price.amount)
  const discountAmount = compareAtPrice > price ? compareAtPrice - price : 0

  return {
    id: line.merchandise.sku,
    name: line.merchandise.product.title,
    brand: line.merchandise.product.vendor,
    category: line.merchandise.product.productType || categoryFallback,
    variant: line.merchandise.title,
    price: price.toFixed(2),
    quantity: String(line.quantity),
    position: (index || 0) + 1,
    list: line.attributes.find((attribute) => attribute.key === SHOPIFY_ATTRIBUTES.LIST)?.value || listFallback,
    product_id: extractLastPart(line.merchandise.product.id),
    variant_id: extractLastPart(line.merchandise.id),
    compare_at_price: discountAmount > 0 ? compareAtPrice.toFixed(2) : priceFallback,
    image: line.merchandise.product.featuredImage?.url || '',
    url: createProductUrl(line.merchandise.product.handle, locale),
  }
}

/**
 * Return first available variant to not send all variants into the data
 * layer. Fall back to first variant if no variants are available.
 */
const extractVariant = (product?: Product) => {
  const variant = product?.variants.find((variant) => variant?.availableForSale) || product?.variants?.[0]
  const price = parseFloat(variant?.price.amount || priceFallback)
  const compareAtPrice = parseFloat(variant?.compareAtPrice?.amount || variant?.price?.amount || priceFallback)
  const discountAmount = compareAtPrice > price ? compareAtPrice - price : 0

  return { variant, price, compareAtPrice, discountAmount }
}
